import { useEffect, useRef, useState, useCallback } from "react";
import { authAxios } from "@/utils/auth";

/**
 * Poll the server-side Learning Profile generation lock so any browser
 * tab / device shows the SAME "generating" state.
 *
 * Backed by `services/learning_profile_lock.py` + `GET
 * /api/learning-profiles/status/{owner_type}/{owner_id}`.
 *
 * Returns the raw status payload plus a `refresh()` for imperative pokes
 * (e.g. right after firing a POST /generate-batch so the button flips to
 * "Generating (0/16)" without waiting for the next 2s tick).
 *
 * IMPORTANT: `refresh()` grants a 30-second **grace period** during which
 * polling continues even when `in_progress: false`. This absorbs the
 * race between the FE optimistically calling `refresh()` and the BE
 * getting far enough into `POST /generate-batch` to actually
 * `SET NX` the Redis key. Without the grace, the first GET after
 * clicking Generate would return `in_progress: false`, stop the poller,
 * and the counter would never update.
 *
 * The hook stops polling when the server reports `in_progress: false`
 * AND the grace window has expired. This keeps idle screens quiet while
 * still giving instant feedback the moment a generation kicks off.
 */
export function useLearningProfileStatus(ownerType, ownerId) {
  const [status, setStatus] = useState({ in_progress: false });
  const intervalRef = useRef(null);
  const cancelledRef = useRef(false);
  // Timestamp in ms until which polling MUST continue even on
  // `in_progress: false`. Set by `refresh()` to bridge the FE-optimistic
  // poll → BE-lock-acquired race window.
  const graceUntilRef = useRef(0);

  const clearPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const fetchStatus = useCallback(async () => {
    if (!ownerType || !ownerId) return null;
    try {
      const res = await authAxios.get(
        `/learning-profiles/status/${ownerType}/${ownerId}`
      );
      if (cancelledRef.current) return null;
      setStatus(res.data || { in_progress: false });
      if (!res.data?.in_progress && Date.now() > graceUntilRef.current) {
        clearPolling();
      }
      return res.data;
    } catch (_e) {
      // Treat any error (401, 404, network) as "not in progress" so the
      // button doesn't stay stuck disabled. Errors are surfaced by the
      // POST caller, not by this poller.
      if (!cancelledRef.current) {
        setStatus({ in_progress: false });
        if (Date.now() > graceUntilRef.current) {
          clearPolling();
        }
      }
      return null;
    }
  }, [ownerType, ownerId]);

  const startPolling = useCallback(() => {
    clearPolling();
    if (!ownerType || !ownerId) return;
    intervalRef.current = setInterval(fetchStatus, 2000);
  }, [ownerType, ownerId, fetchStatus]);

  // Imperative refresh + re-arm polling. Called by consumers right after
  // triggering a generate to get sub-2s feedback and to resume polling
  // even if the previous run's `in_progress:false` had stopped the tick.
  // Grants a 30s grace window so the poller keeps ticking while the BE
  // is still processing the generate-batch request and hasn't yet
  // `SET NX`-ed the Redis lock key.
  const refresh = useCallback(async () => {
    graceUntilRef.current = Date.now() + 30_000;
    startPolling();
    return await fetchStatus();
  }, [fetchStatus, startPolling]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!ownerType || !ownerId) return () => {};
    // Immediate poll on mount so we pick up in-flight jobs after navigation.
    fetchStatus().then((data) => {
      if (data?.in_progress) startPolling();
    });
    return () => {
      cancelledRef.current = true;
      clearPolling();
    };
  }, [ownerType, ownerId, fetchStatus, startPolling]);

  return { ...status, refresh };
}

export default useLearningProfileStatus;
